/**
 * PdfThumbnail.jsx
 * Renders a thumbnail of the first page of a PDF document
 * 
 * Features:
 * - Lazy loading (only renders when visible in viewport)
 * - Session cache for generated thumbnails
 * - Skips PDFs > 5MB (shows icon fallback)
 * - Handles authenticated PDF downloads
 * - Graceful fallback on any errors
 * 
 * Uses pdfjs-dist v4.0.379 installed via yarn
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FileText } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';
import { getStoredAuthToken } from '@/lib/authStorage';

// Configure PDF.js worker - version 4.0.379
pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.0.379/pdf.worker.min.mjs';

// Thumbnail settings
const MAX_WIDTH = 240;
const JPEG_QUALITY = 0.6;
const MAX_PDF_SIZE_FOR_THUMBNAIL = 5 * 1024 * 1024; // 5MB

// Session cache for thumbnails (Map<url, dataUrl>)
const thumbnailCache = new Map();

/**
 * Fetch PDF with authentication and generate thumbnail
 */
async function generateThumbnailFromUrl(pdfUrl) {
  const token = getStoredAuthToken();
  
  // Fetch PDF with auth header
  const response = await fetch(pdfUrl, {
    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
  });
  
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }
  
  // Get PDF as ArrayBuffer
  const pdfData = await response.arrayBuffer();
  
  // Load PDF document with security settings
  const loadingTask = pdfjsLib.getDocument({
    data: pdfData,
    disableAutoFetch: true,
    disableStream: true,
    isEvalSupported: false, // Security: disable eval
    disableFontFace: true   // Performance: don't load fonts
  });
  
  const pdf = await loadingTask.promise;
  const page = await pdf.getPage(1);
  
  // Calculate scale to fit max width
  const viewport = page.getViewport({ scale: 1 });
  const scale = MAX_WIDTH / viewport.width;
  const scaledViewport = page.getViewport({ scale });
  
  // Create off-screen canvas
  const canvas = document.createElement('canvas');
  canvas.width = Math.floor(scaledViewport.width);
  canvas.height = Math.floor(scaledViewport.height);
  
  const context = canvas.getContext('2d');
  
  // Render page to canvas
  await page.render({
    canvasContext: context,
    viewport: scaledViewport
  }).promise;
  
  // Convert to compressed JPEG data URL
  const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  
  // Cleanup PDF resources
  page.cleanup();
  pdf.destroy();
  
  return dataUrl;
}

export function PdfThumbnail({ 
  pdfUrl, 
  fileSize = 0, 
  fileName = 'PDF',
  className = '' 
}) {
  const [thumbnail, setThumbnail] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  
  // Check if PDF is too large for thumbnail (> 5MB)
  const isTooLarge = fileSize > MAX_PDF_SIZE_FOR_THUMBNAIL;
  
  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!containerRef.current || isTooLarge) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );
    
    observer.observe(containerRef.current);
    
    return () => observer.disconnect();
  }, [isTooLarge]);
  
  // Generate thumbnail when component becomes visible
  const generateAndCacheThumbnail = useCallback(async () => {
    // Skip if not ready
    if (!pdfUrl || !isVisible || isTooLarge || loading || thumbnail || error) {
      return;
    }
    
    // Check cache first
    if (thumbnailCache.has(pdfUrl)) {
      setThumbnail(thumbnailCache.get(pdfUrl));
      return;
    }
    
    setLoading(true);
    
    try {
      const dataUrl = await generateThumbnailFromUrl(pdfUrl);
      thumbnailCache.set(pdfUrl, dataUrl);
      setThumbnail(dataUrl);
    } catch (err) {
      // Log error but don't break UI
      console.warn('[PdfThumbnail] Failed to generate thumbnail:', err.message);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [pdfUrl, isVisible, isTooLarge, loading, thumbnail, error]);
  
  useEffect(() => {
    generateAndCacheThumbnail();
  }, [generateAndCacheThumbnail]);
  
  // Fallback: PDF icon
  const renderFallback = () => (
    <div className="w-full h-full flex items-center justify-center bg-slate-100">
      <FileText className="h-6 w-6 text-red-500" />
    </div>
  );
  
  // Loading state: animated icon
  const renderLoading = () => (
    <div className="w-full h-full flex items-center justify-center bg-slate-50">
      <div className="animate-pulse">
        <FileText className="h-6 w-6 text-slate-300" />
      </div>
    </div>
  );
  
  // Too large - skip thumbnail generation entirely
  if (isTooLarge) {
    return (
      <div 
        ref={containerRef} 
        className={className} 
        title={`PDF grande (${(fileSize / (1024*1024)).toFixed(1)}MB)`}
      >
        {renderFallback()}
      </div>
    );
  }
  
  return (
    <div ref={containerRef} className={className}>
      {error ? (
        renderFallback()
      ) : loading ? (
        renderLoading()
      ) : thumbnail ? (
        <img 
          src={thumbnail} 
          alt={`Preview: ${fileName}`}
          className="w-full h-full object-cover rounded"
        />
      ) : (
        renderLoading()
      )}
    </div>
  );
}

export default PdfThumbnail;
